import { useState, useEffect, useRef, useCallback } from 'react';

// Facebook-style lazy loading hook with Intersection Observer
export const useFacebookLazyLoading = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '50px',
    skeletonDuration = 1000, // Minimum time to show skeleton
    staggerDelay = 100, // Delay between items when loading multiple
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const elementRef = useRef();
  const startTimeRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          startTimeRef.current = Date.now();
          
          // Simulate loading time (like Facebook)
          const loadingTime = Math.random() * 800 + 500; // 500-1300ms
          
          setTimeout(() => {
            const elapsedTime = Date.now() - startTimeRef.current;
            const remainingTime = Math.max(0, skeletonDuration - elapsedTime);
            
            setTimeout(() => {
              setIsLoading(false);
              setTimeout(() => setShowContent(true), 50);
            }, remainingTime);
          }, loadingTime);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [threshold, rootMargin, skeletonDuration, isVisible]);

  return {
    elementRef,
    isLoading,
    isVisible,
    showContent,
  };
};

// Hook for progressive loading (like Facebook feed)
export const useProgressiveLazyLoading = (items = [], options = {}) => {
  const {
    batchSize = 3,
    loadDelay = 200,
    skeletonDuration = 800,
  } = options;

  const [loadedItems, setLoadedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);

  const loadMoreItems = useCallback(() => {
    if (loadingRef.current || !hasMore) return;

    loadingRef.current = true;
    setIsLoading(true);

    // Simulate loading delay
    setTimeout(() => {
      const startIndex = loadedItems.length;
      const endIndex = Math.min(startIndex + batchSize, items.length);
      const newItems = items.slice(startIndex, endIndex);

      if (newItems.length === 0) {
        setHasMore(false);
        setIsLoading(false);
        loadingRef.current = false;
        return;
      }

      // Stagger the loading of new items (Facebook style)
      newItems.forEach((item, index) => {
        setTimeout(() => {
          setLoadedItems(prev => [...prev, { ...item, isNew: true }]);
          
          // Remove isNew flag after animation
          setTimeout(() => {
            setLoadedItems(prev => 
              prev.map(loadedItem => 
                loadedItem === item ? { ...loadedItem, isNew: false } : loadedItem
              )
            );
          }, 500);
        }, index * 100);
      });

      setTimeout(() => {
        setIsLoading(false);
        loadingRef.current = false;
      }, skeletonDuration);
    }, loadDelay);
  }, [items, loadedItems, batchSize, loadDelay, skeletonDuration, hasMore]);

  // Initial load
  useEffect(() => {
    if (items.length > 0 && loadedItems.length === 0) {
      loadMoreItems();
    }
  }, [items, loadedItems.length, loadMoreItems]);

  return {
    loadedItems,
    isLoading,
    hasMore,
    loadMoreItems,
  };
};

// Hook for infinite scroll (like Facebook timeline)
export const useInfiniteScroll = (callback, options = {}) => {
  const {
    threshold = 100,
    throttle = 200,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const throttleTimeoutRef = useRef();

  useEffect(() => {
    const handleScroll = () => {
      if (throttleTimeoutRef.current) return;

      throttleTimeoutRef.current = setTimeout(() => {
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = document.documentElement.scrollTop;
        const clientHeight = document.documentElement.clientHeight;

        if (scrollTop + clientHeight >= scrollHeight - threshold && !isLoading) {
          setIsLoading(true);
          callback().finally(() => setIsLoading(false));
        }

        throttleTimeoutRef.current = null;
      }, throttle);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [callback, threshold, throttle, isLoading]);

  return { isLoading, setIsLoading };
};

// Hook for staggered animation (like Facebook posts appearing)
export const useStaggeredAnimation = (items = [], delay = 100) => {
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    if (items.length === 0) return;

    // Clear previous animations
    setVisibleItems([]);

    // Stagger the appearance of items
    items.forEach((item, index) => {
      setTimeout(() => {
        setVisibleItems(prev => [...prev, item]);
      }, index * delay);
    });
  }, [items, delay]);

  return visibleItems;
};

// Main Facebook-style lazy loading component hook
export const useFacebookStyleLoading = (data, options = {}) => {
  const {
    skeletonCount = 3,
    loadingDelay = 500,
    staggerDelay = 100,
  } = options;

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadedData, setLoadedData] = useState([]);
  const [showSkeletons, setShowSkeletons] = useState(true);

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Show skeletons initially
    setShowSkeletons(true);
    setIsInitialLoading(true);

    // Simulate loading time
    setTimeout(() => {
      setShowSkeletons(false);
      
      // Stagger the appearance of actual content
      data.forEach((item, index) => {
        setTimeout(() => {
          setLoadedData(prev => [...prev, item]);
        }, index * staggerDelay);
      });

      setIsInitialLoading(false);
    }, loadingDelay);
  }, [data, loadingDelay, staggerDelay]);

  return {
    isInitialLoading,
    loadedData,
    showSkeletons,
    skeletonCount,
  };
};

export default useFacebookLazyLoading;
